import React, { useEffect, useState, useRef } from 'react';
import { Users, Target, Heart, Star } from 'lucide-react';
import { mockData } from '../mock';
import { useLanguage } from '../contexts/LanguageContext';

const EventimeAbout = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section id="about" className="section section-alt" ref={sectionRef}>
      <div className="container">
        <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-giant)' }}>
          <h2 
            className={`heading-1 animated ${isVisible ? 'fadeIn' : ''}`}
            style={{ marginBottom: 'var(--spacing-giant)' }}
          >
            {t('about.title')}
          </h2>
          
          {/* Vision Section */}
          <div 
            className={`animated ${isVisible ? 'fadeIn' : ''}`}
            style={{ 
              maxWidth: '1000px', 
              margin: '0 auto var(--spacing-giant) auto',
              transitionDelay: '0.2s'
            }}
          >
            <h3 
              className="heading-2" 
              style={{ 
                marginBottom: 'var(--spacing-medium)',
                color: 'var(--brand-dark)'
              }}
            >
              {t('about.vision.title')}
            </h3>
            <div style={{
              background: 'var(--bg-card)',
              borderRadius: '20px',
              padding: 'var(--spacing-xl)',
              border: '2px solid var(--border-light)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.05)',
              marginBottom: 'var(--spacing-giant)'
            }}>
              <p className="body-large" style={{ 
                lineHeight: '1.8',
                fontSize: '1.2rem',
                color: 'var(--text-primary)'
              }}>
                {t('about.vision.description')}
              </p>
            </div>
          </div>

          {/* Mission Section */}
          <div 
            className={`animated ${isVisible ? 'fadeIn' : ''}`}
            style={{ 
              maxWidth: '1000px', 
              margin: '0 auto',
              transitionDelay: '0.3s'
            }}
          >
            <h3 
              className="heading-2" 
              style={{ 
                marginBottom: 'var(--spacing-medium)',
                color: 'var(--brand-dark)'
              }}
            >
              {t('about.mission.title')}
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: 'var(--spacing-large)',
              marginBottom: 'var(--spacing-giant)'
            }}>
              <div style={{
                background: 'var(--bg-card)',
                borderRadius: '16px',
                padding: 'var(--spacing-large)',
                border: '1px solid var(--border-light)',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
                textAlign: 'left'
              }}>
                <p 
                  className="body-large" 
                  style={{ 
                    lineHeight: '1.7',
                    color: 'var(--text-primary)'
                  }}
                  dangerouslySetInnerHTML={{ __html: t('about.mission.connect') }}
                />
              </div>
              
              <div style={{
                background: 'var(--bg-card)',
                borderRadius: '16px',
                padding: 'var(--spacing-large)',
                border: '1px solid var(--border-light)',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
                textAlign: 'left'
              }}>
                <p 
                  className="body-large" 
                  style={{ 
                    lineHeight: '1.7',
                    color: 'var(--text-primary)'
                  }}
                  dangerouslySetInnerHTML={{ __html: t('about.mission.promote') }}
                />
              </div>
              
              <div style={{
                background: 'var(--bg-card)',
                borderRadius: '16px',
                padding: 'var(--spacing-large)',
                border: '1px solid var(--border-light)',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
                textAlign: 'left'
              }}>
                <p 
                  className="body-large" 
                  style={{ 
                    lineHeight: '1.7',
                    color: 'var(--text-primary)'
                  }}
                  dangerouslySetInnerHTML={{ __html: t('about.mission.execute') }}
                />
              </div>
            </div>
          </div>

        </div>

        {/* Enhanced Values Cards */}
        <div 
          className="network-grid"
          style={{ 
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 'var(--spacing-large)',
            padding: '0'
          }}
        >
          <div 
            className={`network-card hover-lift animated ${isVisible ? 'fadeIn' : ''}`}
            style={{ transitionDelay: '0.3s' }}
          >
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              marginBottom: 'var(--spacing-medium)' 
            }}>
              <div style={{
                background: 'linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-primary-light) 100%)',
                borderRadius: '16px',
                padding: '16px',
                marginRight: 'var(--spacing-medium)',
                color: 'var(--text-white)',
                boxShadow: 'var(--shadow-medium)'
              }}>
                <Users size={28} className="icon-enhanced" />
              </div>
              <h3 className="network-card-title" style={{ marginBottom: 0 }}>
                {t('about.values.community.title')}
              </h3>
            </div>
            <p className="network-card-content">
              {t('about.values.community.description')}
            </p>
          </div>

          <div 
            className={`network-card hover-lift animated ${isVisible ? 'fadeIn' : ''}`}
            style={{ transitionDelay: '0.4s' }}
          >
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              marginBottom: 'var(--spacing-medium)' 
            }}>
              <div style={{
                background: 'linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-primary-light) 100%)',
                borderRadius: '16px',
                padding: '16px',
                marginRight: 'var(--spacing-medium)',
                color: 'var(--text-white)',
                boxShadow: 'var(--shadow-medium)'
              }}>
                <Target size={28} className="icon-enhanced" />
              </div>
              <h3 className="network-card-title" style={{ marginBottom: 0 }}>
                {t('about.values.professional.title')}
              </h3>
            </div>
            <p className="network-card-content">
              {t('about.values.professional.description')}
            </p>
          </div>

          <div 
            className={`network-card hover-lift animated ${isVisible ? 'fadeIn' : ''}`}
            style={{ transitionDelay: '0.5s' }}
          >
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              marginBottom: 'var(--spacing-medium)' 
            }}>
              <div style={{
                background: 'linear-gradient(135deg, var(--brand-primary) 0%, var(--brand-primary-light) 100%)',
                borderRadius: '16px',
                padding: '16px',
                marginRight: 'var(--spacing-medium)',
                color: 'var(--text-white)',
                boxShadow: 'var(--shadow-medium)'
              }}>
                <Heart size={28} className="icon-enhanced" />
              </div>
              <h3 className="network-card-title" style={{ marginBottom: 0 }}>
                {t('about.values.passion.title')}
              </h3>
            </div>
            <p className="network-card-content">
              {t('about.values.passion.description')}
            </p>
          </div>
        </div>

        {/* Why Choose Eventime Section */}
        <div 
          className={`animated ${isVisible ? 'fadeIn' : ''}`}
          style={{
            marginTop: 'var(--spacing-giant)',
            textAlign: 'center',
            transitionDelay: '0.6s'
          }}
        >
          <div 
            style={{
              background: 'linear-gradient(135deg, var(--bg-card) 0%, #ffffff 100%)',
              borderRadius: '32px',
              padding: 'var(--spacing-xl)',
              maxWidth: '1000px',
              margin: '0 auto',
              boxShadow: 'var(--shadow-heavy)',
              border: '3px solid var(--brand-secondary)'
            }}
          >
            <h3 
              className="heading-2" 
              style={{ 
                marginBottom: 'var(--spacing-large)',
                color: 'var(--brand-dark)'
              }}
            >
              {t('services.why.title')}
            </h3>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: 'var(--spacing-large)',
              textAlign: 'left'
            }}>
              <div style={{
                padding: 'var(--spacing-medium)',
                background: 'rgba(241, 196, 15, 0.1)',
                borderRadius: '20px',
                border: '2px solid var(--brand-secondary)'
              }}>
                <h4 className="heading-3" style={{ 
                  color: 'var(--brand-dark)', 
                  marginBottom: 'var(--spacing-xs)' 
                }}>
                  {t('services.why.experience.title')}
                </h4>
                <p className="body-medium">
                  {t('services.why.experience.description')}
                </p>
              </div>
              
              <div style={{
                padding: 'var(--spacing-medium)',
                background: 'rgba(241, 196, 15, 0.1)',
                borderRadius: '20px',
                border: '2px solid var(--brand-secondary)'
              }}>
                <h4 className="heading-3" style={{ 
                  color: 'var(--brand-dark)', 
                  marginBottom: 'var(--spacing-xs)' 
                }}>
                  {t('services.why.team.title')}
                </h4>
                <p className="body-medium">
                  {t('services.why.team.description')}
                </p>
              </div>
              
              <div style={{
                padding: 'var(--spacing-medium)',
                background: 'rgba(241, 196, 15, 0.1)',
                borderRadius: '20px',
                border: '2px solid var(--brand-secondary)'
              }}>
                <h4 className="heading-3" style={{ 
                  color: 'var(--brand-dark)', 
                  marginBottom: 'var(--spacing-xs)' 
                }}>
                  {t('services.why.technology.title')}
                </h4>
                <p className="body-medium">
                  {t('services.why.technology.description')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Mission Call-to-Action */}
        <div 
          className={`animated ${isVisible ? 'fadeIn' : ''}`}
          style={{ 
            textAlign: 'center', 
            marginTop: 'var(--spacing-giant)',
            transitionDelay: '0.7s'
          }}
        >
          <div 
            style={{
              background: 'linear-gradient(135deg, var(--bg-card) 0%, #ffffff 100%)',
              borderRadius: '32px',
              padding: 'var(--spacing-xl)',
              maxWidth: '900px',
              margin: '0 auto',
              boxShadow: 'var(--shadow-heavy)',
              border: '3px solid var(--brand-secondary)',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Decorative elements */}
            <div style={{
              position: 'absolute',
              top: '-20px',
              left: '-20px',
              width: '60px',
              height: '60px',
              background: 'var(--brand-primary)',
              borderRadius: '50%',
              opacity: '0.1'
            }} />
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              right: '-30px',
              width: '80px',
              height: '80px',
              background: 'var(--brand-dark)',
              borderRadius: '50%',
              opacity: '0.05'
            }} />
            
            <h3 
              className="heading-2" 
              style={{ 
                marginBottom: 'var(--spacing-medium)',
                color: 'var(--brand-dark)',
                position: 'relative',
                zIndex: 2
              }}
            >
              {t('about.cta.title')}
            </h3>
            <p 
              className="body-large" 
              style={{ 
                position: 'relative',
                zIndex: 2
              }}
            >
              {t('about.cta.description')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EventimeAbout;